import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { Estado, SemPermissao, Vazio } from '../../componentes/estados';
import { Entrada } from '../../componentes/formulario';
import { Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { SeloVerticais, useEmpresasDoCatalogo } from '../../componentes/verticais';
import { dinheiro, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { CabecalhoConfig } from './config-crm-base';
import { mensagemDeErro } from '../../nucleo/api';
import { FormServicoCatalogo, useCatalogoVenda, useGravarServico, type ServicoCatalogo } from './FormServicoCatalogo';

/**
 * Lista de 03/11, item 9: Produtos e Serviços nas Configurações do CRM. Lista o catálogo de venda do Cadastros, com busca, e
 * cria e edita com o MESMO formulário do botão "Criar novo produto/serviço" da negociação (`FormServicoCatalogo`), com os
 * campos personalizados do catálogo. Ver `cadastros.ver`, criar `cadastros.criar`, editar `cadastros.editar` (as da
 * rota); sem o Cadastros instalado a seção nem existe (o atalho do índice some).
 */
export function ProdutosServicosCrm() {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const catalogo = useCatalogoVenda();
  const verticais = useEmpresasDoCatalogo();
  const gravar = useGravarServico(s.workspace?.moeda ?? 'BRL');
  const [busca, setBusca] = useState('');
  const [aberto, setAberto] = useState<ServicoCatalogo | 'novo' | null>(null);
  // Cancelar devolve o foco ao botão que abriu (criar ou o lápis da linha).
  const origem = useRef<HTMLElement | null>(null);
  const abrir = (x: ServicoCatalogo | 'novo', el: HTMLElement | null) => {
    origem.current = el;
    setAberto(x);
  };
  const fechar = () => {
    setAberto(null);
    queueMicrotask(() => origem.current?.focus());
  };
  const podeCriar = s.pode('cadastros.criar');
  const podeEditar = s.pode('cadastros.editar');
  if (!catalogo.podeCatalogo) return <SemPermissao />;
  const norm = (x: string) => x.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const novo = podeCriar && (
    <Botao variante="primario" icone={<Plus className="size-4" />} onClick={(e) => abrir('novo', e.currentTarget)} data-criar-servico-config="">
      {t('crm.cfg.produtos.criar')}
    </Botao>
  );
  const visiveis = verticais.data?.empresas ?? [];
  return (
    <section className="flex min-w-0 flex-col gap-5" data-config-produtos="">
      <CabecalhoConfig titulo={t('crm.cfg.produtos.titulo')} acao={novo || null} intro={<p>{t('crm.cfg.produtos.intro')}</p>} />
      <Entrada type="search" value={busca} placeholder={t('crm.cfg.produtos.buscar')} aria-label={t('crm.cfg.produtos.buscar')} onChange={(e) => setBusca(e.target.value)} data-buscar-servico-config="" />
      <Estado consulta={catalogo}>
        {(itens) => {
          const lista = itens.filter((x) => !busca.trim() || norm(`${x.nome} ${x.codigo ?? ''}`).includes(norm(busca.trim())));
          if (itens.length === 0) return <Vazio texto={t('crm.cfg.produtos.vazio')} />;
          if (lista.length === 0) return <Vazio texto={t('crm.cfg.produtos.semResultado')} />;
          return (
            <ul className="divide-y divide-borda rounded-xl border border-borda bg-sup-1" data-lista-servicos-config="">
              {lista.map((x) => (
                <li key={x.id} className="flex min-w-0 items-center gap-2 pr-2" data-servico-config={x.nome}>
                  <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
                    <span className="flex min-w-0 flex-1 basis-44 flex-col">
                      <span className="truncate font-medium" title={textoLivre(x.nome)}>
                        {textoLivre(x.nome)}
                      </span>
                      {x.codigo && <span className="numero truncate text-xs text-tinta-3">{x.codigo}</span>}
                    </span>
                    {x.tipo_cobranca && <Selo>{t(`crm.ficha2.cobranca.${x.tipo_cobranca === 'recorrente' ? 'recorrente' : 'avulso'}`)}</Selo>}
                    <SeloVerticais empresas={x.empresas} catalogo={visiveis} />
                    <span className="numero shrink-0 text-sm font-semibold">{dinheiro(x.preco_venda_centavos, x.moeda ?? s.workspace?.moeda ?? 'BRL')}</span>
                  </span>
                  {podeEditar && (
                    <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.cfg.produtos.editar', { nome: textoLivre(x.nome) })} title={t('crm.cfg.produtos.editar', { nome: textoLivre(x.nome) })} icone={<Pencil className="size-4" />} onClick={(e) => abrir(x, e.currentTarget)} data-editar-servico-config={x.nome} />
                  )}
                </li>
              ))}
            </ul>
          );
        }}
      </Estado>
      <Drawer aberto={aberto !== null} aoFechar={fechar} titulo={aberto && aberto !== 'novo' ? t('crm.cfg.produtos.editarTitulo') : t('crm.cfg.produtos.criarTitulo')}>
        {aberto !== null && verticais.isError && (
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-sm text-tinta-3" role="alert" data-verticais-erro="">
            <span>{mensagemDeErro(verticais.error)}</span>
            <Botao tamanho="sm" variante="secundario" onClick={() => void verticais.refetch()}>
              {tn('estados.tentarDeNovo')}
            </Botao>
          </div>
        )}
        {aberto !== null && (
          <FormServicoCatalogo
            key={aberto === 'novo' ? 'novo' : aberto.id}
            visiveis={visiveis}
            atual={aberto === 'novo' ? null : aberto}
            comCampos
            confirmarDesabilitado={!verticais.isSuccess}
            rotuloConfirmar={tn('comum.salvar')}
            varianteConfirmar="primario"
            aoCancelar={fechar}
            aoGravar={(d) => (aberto === 'novo' ? gravar.criar(d) : gravar.editar(aberto, d))}
            aoGravado={() => {
              toast.sucesso(t('crm.cfg.produtos.salvo'));
              fechar();
            }}
          />
        )}
      </Drawer>
    </section>
  );
}
